import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { usePostHogWidget } from "widgets/shared/usePostHogWidget";

type TrackProperties = Partial<Record<string, string | number>>;

/**
 * Identity for a single Tripper widget instance. Attached to every event so
 * attribution is correct even when several Tripper widgets share the page-wide
 * PostHog singleton (global super-properties would be overwritten by whichever
 * widget registered last).
 */
type TripperTrackingIdentity = {
  organizationId: string;
  widgetId: string;
  itineraryId: string;
};

type TripperTrackingContextValue = {
  track: (event: string, properties?: TrackProperties) => void;
};

const TripperTrackingContext =
  createContext<TripperTrackingContextValue | null>(null);

export const TripperTrackingProvider = ({
  organizationId,
  widgetId,
  itineraryId,
  children,
}: PropsWithChildren<TripperTrackingIdentity>) => {
  const { posthog } = usePostHogWidget();

  const track = useCallback(
    (event: string, properties?: TrackProperties) => {
      posthog.capture(event, {
        appContext: "widget_tripper",
        organizationId,
        widgetId,
        itineraryId,
        ...properties,
      });
    },
    [posthog, organizationId, widgetId, itineraryId],
  );

  const value = useMemo<TripperTrackingContextValue>(
    () => ({ track }),
    [track],
  );

  return (
    <TripperTrackingContext.Provider value={value}>
      {children}
    </TripperTrackingContext.Provider>
  );
};

/**
 * Returns a `track(event, props)` function that merges this widget's identity
 * into every event. Safe to call outside a provider (no-op) so launcher
 * components rendered before data loads don't throw.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useTripperTrack = () => {
  const context = useContext(TripperTrackingContext);
  return context?.track ?? noopTrack;
};

const noopTrack = () => {};
